import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Tabs,
  message,
  Spin,
  Switch,
  Select,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, SendOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import settingsService, { type EmailTemplateRow } from '@/services/settingsService';
import { useAuthStore } from '@/store/authStore';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { TabPane } = Tabs;

export const EmailTemplatesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = Boolean(user?.is_super_admin);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isCreate, setIsCreate] = useState(false);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [previewRecord, setPreviewRecord] = useState<EmailTemplateRow | null>(null);
  const [form] = Form.useForm();

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['email-templates'],
    queryFn: () => settingsService.getEmailTemplates(),
  });

  const saveMutation = useMutation({
    mutationFn: (payload: { id: string; subject: string; bodyHtml: string; is_active: boolean }) =>
      settingsService.updateEmailTemplate(payload.id, {
        subject: payload.subject,
        bodyHtml: payload.bodyHtml,
        is_active: payload.is_active,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    },
  });

  const createMutation = useMutation({
    mutationFn: (payload: Parameters<typeof settingsService.createEmailTemplate>[0]) => settingsService.createEmailTemplate(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => settingsService.deleteEmailTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    },
  });

  const handleAdd = () => {
    form.resetFields();
    form.setFieldsValue({
      is_active: true,
      scope: 'company',
    });
    setIsCreate(true);
    setIsModalVisible(true);
  };

  const handleEdit = (record: EmailTemplateRow) => {
    setIsCreate(false);
    form.setFieldsValue({
      id: record.id,
      template_name: record.template_name,
      template_key: record.template_key,
      category: record.category,
      subject: record.subject,
      body: record.body,
      is_active: record.is_active,
      is_system: record.is_system,
    });
    setIsModalVisible(true);
  };

  const handleSave = async (values: Record<string, unknown>) => {
    try {
      if (isCreate) {
        const name = String(values.template_name ?? '').trim();
        const code = String(values.template_key ?? '').trim();
        const eventType = String(values.category ?? '').trim();
        if (!name || !code || !eventType) {
          message.error('Template name, key, and category (event type) are required');
          return;
        }
        if (isSuperAdmin && values.scope === 'company' && !user?.company_id) {
          message.error('Your user has no company. Use a global template or a company user.');
          return;
        }
        const globalScope = isSuperAdmin && values.scope === 'global';
        await createMutation.mutateAsync({
          template_name: name,
          template_code: code,
          event_type: eventType,
          subject: String(values.subject ?? ''),
          body: String(values.body ?? ''),
          is_active: values.is_active !== false,
          company_id: globalScope ? null : (user?.company_id ?? null),
        });
        message.success('Email template created');
      } else {
        const id = values.id != null ? String(values.id) : '';
        if (!id) {
          message.error('Missing template id');
          return;
        }
        await saveMutation.mutateAsync({
          id,
          subject: String(values.subject ?? ''),
          bodyHtml: String(values.body ?? ''),
          is_active: values.is_active !== false,
        });
        message.success('Email template saved successfully.');
      }
      setIsModalVisible(false);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(err?.response?.data?.message || err?.message || 'Save failed');
    }
  };

  const handlePreview = (record: EmailTemplateRow) => {
    setPreviewRecord(record);
    setIsPreviewVisible(true);
  };

  const columns: ColumnsType<EmailTemplateRow> = [
    {
      title: 'Template Name',
      dataIndex: 'template_name',
      key: 'template_name',
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => {
        const colors: Record<string, string> = {
          invoicing: 'blue',
          payment: 'green',
          approval: 'orange',
          user_management: 'purple',
        };
        const key = (category || '').toLowerCase().replace(/\s+/g, '_');
        return <Tag color={colors[key] || 'default'}>{(category || '').replace(/_/g, ' ')}</Tag>;
      },
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active: boolean) => <Tag color={active ? 'success' : 'default'}>{active ? 'Active' : 'Inactive'}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EyeOutlined />} onClick={() => handlePreview(record)}>
            Preview
          </Button>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            Edit
          </Button>
          <Button type="link" danger icon={<DeleteOutlined />} disabled>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="Email Templates"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Create Template
          </Button>
        }
      >
        <Spin spinning={isLoading}>
          <Table
            columns={columns as ColumnsType<EmailTemplateRow>}
            dataSource={templates}
            rowKey="id"
            pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
          />
        </Spin>
      </Card>

      <Modal
        title={isCreate ? 'Create email template' : 'Email template editor'}
        open={isModalVisible}
        onOk={() => form.submit()}
        onCancel={() => setIsModalVisible(false)}
        width={900}
        okText={isCreate ? 'Create' : 'Save template'}
        confirmLoading={saveMutation.isPending || createMutation.isPending}
      >
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="id" hidden>
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="is_system" hidden>
            <Input type="hidden" />
          </Form.Item>
          {isCreate && isSuperAdmin ? (
            <Form.Item label="Scope" name="scope" initialValue="company">
              <Select
                options={[
                  { value: 'company', label: 'This company' },
                  { value: 'global', label: 'Global (all companies) — not tied to a tenant' },
                ]}
              />
            </Form.Item>
          ) : null}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Template name"
                name="template_name"
                rules={isCreate ? [{ required: true, message: 'Name is required' }] : undefined}
              >
                <Input disabled={!isCreate} placeholder="e.g. Quotation follow-up" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Template key (code)"
                name="template_key"
                extra={isCreate ? 'Letters, numbers, and underscores. Must be unique in this scope.' : undefined}
                rules={isCreate ? [{ required: true, message: 'Key is required' }] : undefined}
              >
                <Input disabled={!isCreate} placeholder="e.g. sales_quotation_reminder" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Category (event type)"
                name="category"
                rules={isCreate ? [{ required: true, message: 'Event type is required' }] : undefined}
              >
                <Input
                  disabled={!isCreate}
                  placeholder="e.g. sales_notification, user_management"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Status" name="is_active" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Email Subject" name="subject" rules={[{ required: true }]}>
            <Input placeholder="e.g., Invoice {{invoice_number}} from {{company_name}}" />
          </Form.Item>

          <Form.Item
            label="Email Body (HTML)"
            name="body"
            rules={[{ required: true }]}
            extra="Use {{variable_name}} for dynamic content. Supports HTML."
          >
            <TextArea
              rows={12}
              placeholder={`<html>
<body>
  <p>Dear {{customer_name}},</p>
  <p>Your invoice is ready...</p>
</body>
</html>`}
              style={{ fontFamily: 'monospace', fontSize: '13px' }}
            />
          </Form.Item>

          <Card size="small" title="Available Variables" style={{ marginTop: 16 }}>
            <Space wrap>
              <Tag>{`{{company_name}}`}</Tag>
              <Tag>{`{{customer_name}}`}</Tag>
              <Tag>{`{{invoice_number}}`}</Tag>
              <Tag>{`{{amount}}`}</Tag>
              <Tag>{`{{date}}`}</Tag>
              <Tag>{`{{user_name}}`}</Tag>
            </Space>
            <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              These variables will be replaced with actual values when the email is sent.
            </Text>
          </Card>
        </Form>
      </Modal>

      <Modal
        title="Email Preview"
        open={isPreviewVisible}
        onCancel={() => setIsPreviewVisible(false)}
        width={700}
        footer={[
          <Button key="close" onClick={() => setIsPreviewVisible(false)}>
            Close
          </Button>,
          <Button key="test" type="primary" icon={<SendOutlined />} disabled>
            Send Test Email
          </Button>,
        ]}
      >
        <Tabs defaultActiveKey="1">
          <TabPane tab="Preview" key="1">
            <Card>
              <Title level={5}>Subject:</Title>
              <Text>{previewRecord?.subject}</Text>
              <Title level={5} style={{ marginTop: 16 }}>
                Body:
              </Title>
              <div
                style={{
                  border: '1px solid #d9d9d9',
                  borderRadius: '4px',
                  padding: '16px',
                  background: '#fff',
                }}
                dangerouslySetInnerHTML={{
                  __html: previewRecord?.body || '<p>No content</p>',
                }}
              />
            </Card>
          </TabPane>
          <TabPane tab="HTML Source" key="2">
            <TextArea
              value={previewRecord?.body || ''}
              rows={15}
              readOnly
              style={{ fontFamily: 'monospace', fontSize: '13px' }}
            />
          </TabPane>
        </Tabs>
      </Modal>
    </div>
  );
};
